import * as Yup from "yup";
import { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useCallback, useMemo } from "react";
import { Dialog } from "../../../components/Dialog";
import { useStyles } from "../styles";
import { Autocomplete } from "../../../components/Autocomplete";
import { listStores } from "../../../services/insights";
import { useMutation, useQuery } from "react-query";
import { useFormik } from "formik";
import { CreateInstanceWhatsAppData } from "../../../types/whatsApp";
import { Typography } from "@mui/material";
import { createInstance } from "../../../services/whatsApp";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";

type NewWhatsAppProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  instance_type: Yup.number().required("Tipo da instância obrigatório"),
  name: Yup.string().required("Nome da instância obrigatório"),
  store: Yup.number().required("Loja obrigatório"),
});

export function NewWhatsApp({ open, onClose }: NewWhatsAppProps) {
  const styles = useStyles();
  const toast = useToast();

  const newInstance = useMutation(
    async (data: CreateInstanceWhatsAppData) => {
      const response = await createInstance(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Instância criada com sucesso!", { severity: "success" });
        queryClient.invalidateQueries("whatsAppInstances");
        onClose();
      },
      onError: () => {
        toast.show("Falha ao criar instância", { severity: "error" });
      },
    }
  );

  const handleSave = useCallback(
    async (data: CreateInstanceWhatsAppData) => {
      await newInstance.mutateAsync(data);
    },
    [newInstance]
  );

  const formik = useFormik<CreateInstanceWhatsAppData>({
    initialValues: { instance_type: 1 } as CreateInstanceWhatsAppData,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSave,
  });

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Fechar",
        onClick: onClose,
      },
      {
        variant: "contained",
        disabled: formik.isSubmitting,
        children: formik.isSubmitting ? "Enviando solicitação" : "Criar",
        onClick: () => formik.handleSubmit(),
      },
    ];
  }, [onClose, formik]);

  const stores = useQuery("stores", listStores);

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title="Nova instância"
      open={open}
      actions={actions}
    >
      <Stack spacing={4} className={styles.rootContent}>
        <Autocomplete
          size="small"
          label="Lojas"
          loading={stores.isLoading}
          options={stores.data}
          getOptionLabel={(option) => option.nome_loja}
          onChange={(_, value) => {
            formik.setFieldValue("name", value?.nome_loja);
            formik.setFieldValue("store", value?.id_loja);
          }}
        />

        {formik.errors.name && (
          <Typography variant="button" color="red">
            {formik.errors.name}
          </Typography>
        )}
        {formik.errors.store && (
          <Typography variant="button" color="red">
            {formik.errors.store}
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}
